import * as THREE from 'three';
import type { Viewer } from './viewer';
import type { Renderer as PotreeLowLevelRenderer } from '../PotreeRenderer';

/**
 * Identifies which scene-overlay passes a pipeline should run for a given
 * render. Widgets typically want only the point cloud; the main view wants
 * everything (controls, gizmos, floorplan, clip-volume meshes, ...).
 */
export interface OverlayGates {
  measurements: boolean;
  planes: boolean;
  volumes: boolean;
  controls: boolean;
  clippingVolumeMesh: boolean;
  gizmo: boolean;
  floorplan: boolean;
  /**
   * Scenes registered via `viewer.registerToolOverlayScene` (line prompts,
   * plane previews, floor previews). Off for views that should show only
   * the cloud + standard overlays without tool-specific previews.
   */
  toolOverlay: boolean;
  /** sceneDebug — always-on-top debug elements */
  debug: boolean;
}

export const ALL_OVERLAYS_ON: OverlayGates = {
  measurements: true,
  planes: true,
  volumes: true,
  controls: true,
  clippingVolumeMesh: true,
  gizmo: true,
  floorplan: true,
  toolOverlay: true,
  debug: true,
};

export const POINTCLOUD_ONLY_OVERLAYS: OverlayGates = {
  measurements: false,
  planes: false,
  volumes: false,
  controls: false,
  clippingVolumeMesh: false,
  gizmo: false,
  floorplan: false,
  toolOverlay: false,
  debug: false,
};

/**
 * Per-render context handed to a RenderPipeline.
 *
 * Anything that varies per RenderView lives here; anything shared across
 * views (the THREE.WebGLRenderer, the low-level Potree Renderer, the scene,
 * dispatchEvent, EDL config, etc.) is reached through `viewer`.
 *
 * Keeping `viewer` in the context (rather than copying every field) is
 * deliberate: pipelines today read ~20 viewer fields, and forwarding all of
 * them turns into an interface-mirror that drifts. The trade-off is that
 * pipelines can mutate viewer state — they must not. Treat `viewer` as
 * read-only inside pipelines.
 */
export interface RenderPipelineContext {
  /** The shared THREE.WebGLRenderer. Pipelines read its size from ctx.width/height instead of getSize(). */
  threeRenderer: THREE.WebGLRenderer;
  /** The shared low-level Potree renderer (handles point-cloud draw calls). */
  pRenderer: PotreeLowLevelRenderer;
  /** Read-only access to viewer for shared state (scene graph, EDL config, overlay scenes). */
  viewer: Viewer;
  /** Camera for this render — replaces scene.getActiveCamera(). */
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  /** Render-target width in pixels. Replaces threeRenderer.getSize(). */
  width: number;
  /** Render-target height in pixels. */
  height: number;
  /**
   * Sphere clip volumes to pass to pRenderer.render. Defaults to scene-derived;
   * an alternate set comes from the RenderView's clipOverride.
   */
  clipSpheres: THREE.Object3D[];
  /** Which overlay scenes the pipeline should draw. */
  overlays: OverlayGates;
}

/**
 * A render strategy (EDL / plain Potree / HQ splat).
 *
 * Implementations are stateless w.r.t. per-view data — render targets and
 * other per-view storage live in `RenderPipelineState` and are owned by
 * the RenderView, so one pipeline class instance can drive many views.
 */
export interface RenderPipeline<TState extends RenderPipelineState = RenderPipelineState> {
  /** Stable identifier — useful for diagnostics and for swapping pipelines without churn. */
  readonly kind: 'potree' | 'edl' | 'hq';
  /** Allocate this pipeline's per-view state (RTs, materials, etc.). */
  createState(): TState;
  /** Render one frame for one view. */
  render(ctx: RenderPipelineContext, state: TState): void;
  /** Clear the canvas / RTs at the start of a frame. */
  clear(ctx: RenderPipelineContext, state: TState): void;
  /** Release GPU resources held by this state object. */
  disposeState(state: TState): void;
}

/**
 * Marker base for per-view pipeline state. Each pipeline narrows this with
 * its own RTs and helpers (e.g. EDLPipelineState carries rtEDL/rtRegular).
 */
export interface RenderPipelineState {
  readonly kind: 'potree' | 'edl' | 'hq';
}
